import { useMemo } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { FOLDER_SIDEBAR_WIDTH } from 'framework-listing-lib/constants/folders';
import AsyncCrmListingFolders from 'framework-listing-lib/internal/Folders/components/AsyncCrmListingFolders';
import useShowLoadingSkeleton from 'framework-listing-lib/internal/hooks/useShowLoadingSkeleton';
import CrmListingFoldersSkeleton from 'framework-listing-lib/internal/Folders/components/CrmListingFoldersSkeleton';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useTableDimensions from 'framework-listing-lib/internal/Table/hooks/useTableDimensions';
import { useHasFoldersTab } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import { DEFAULT_PADDING_TOKEN, TABLE_MARGIN_BOTTOM } from 'framework-listing-lib/constants/sizes';
import { EMPTY_STATE_TYPE } from 'framework-listing-lib/constants/emptyState';
import useEmptyState from 'framework-listing-lib/hooks/useEmptyState';

// Filter bar height + default padding
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FILTER_BAR_BLOCK_SPACING_TOKEN = '1400';
export default function LayoutWithFoldersSidebar({
  children,
  maxHeight = 'full-height'
}) {
  const hasFoldersTab = useHasFoldersTab();
  const isFoldersMode = useIsFoldersMode();
  const showLoadingSkeleton = useShowLoadingSkeleton();
  const hasViews = useHasViews();
  const {
    emptyStateResult
  } = useEmptyState();
  const isFirstTimeInApp = useMemo(() => Boolean(emptyStateResult && emptyStateResult.emptyStateType === EMPTY_STATE_TYPE.FIRST_TIME_IN_APP), [emptyStateResult]);
  const {
    height,
    refCallback
  } = useTableDimensions({
    heightAffordances: TABLE_MARGIN_BOTTOM,
    // The bordered folders container should always have a min-height
    // equivalent to full-height mode
    maxHeight: hasFoldersTab ? 'full-height' : maxHeight
  });
  return /*#__PURE__*/_jsx("div", {
    ref: refCallback
    // Replicates UIFlex > UIFlex styles from pre-trellis migration
    ,
    style: {
      width: 'auto',
      maxWidth: '100%',
      flexGrow: 1
    },
    children: /*#__PURE__*/_jsxs(Flex, Object.assign({
      "data-test-id": "layout-with-folders-sidebar",
      flexDirection: "row",
      position: "relative",
      marginBlockStart: !isFirstTimeInApp ? `-${FILTER_BAR_BLOCK_SPACING_TOKEN}` : '0',
      gap: DEFAULT_PADDING_TOKEN
    }, hasFoldersTab && isFoldersMode && {
      borderWidth: '100',
      borderStyle: 'solid',
      borderColor: 'border-core-subtle',
      borderBlockStartWidth: '0'
    }, {
      minBlockSize: hasFoldersTab ? height : undefined,
      children: [isFoldersMode && !isFirstTimeInApp && /*#__PURE__*/_jsx(Flex, {
        as: "section",
        flex: `0 0 ${FOLDER_SIDEBAR_WIDTH}px`,
        inlineSize: "100%",
        paddingBlockStart: hasViews ? '0' : DEFAULT_PADDING_TOKEN,
        children: showLoadingSkeleton ? /*#__PURE__*/_jsx(CrmListingFoldersSkeleton, {}) : /*#__PURE__*/_jsx(AsyncCrmListingFolders, {
          maxHeight: maxHeight
        })
      }), /*#__PURE__*/_jsx(Flex, {
        as: "section",
        flex: "1 1 100%",
        overflow: "hidden",
        inlineSize: "100%",
        paddingBlockStart: !isFirstTimeInApp ? FILTER_BAR_BLOCK_SPACING_TOKEN : undefined,
        children: children
      })]
    }))
  });
}